'use client';

import * as React from 'react';
import { HTMLAttributes } from 'react';
import classNames from 'classnames';
import { IButtonProps } from '../Button';
import { IButtonIconProps } from '../ButtonIcon';
import { IButtonsPrimaryProps } from './Primary';
import { IButtonsSecondaryProps } from './Secondary';
import './styles.scss';

export type ButtonChildren =
  | React.ReactElement<IButtonProps | IButtonIconProps | IButtonsPrimaryProps | IButtonsSecondaryProps>
  | boolean
  | undefined
  | null;

export interface IButtonsProps {
  /** Managed children */
  children: ButtonChildren | ButtonChildren[];
  /** size to use */
  size?: 'default' | 'small';
  /** Layout to use */
  layout?: 'default' | 'horizontal' | 'centered' | 'vertical' | 'vertical-wide';
  /** className to append */
  className?: string;
  /** Make it stick on mobile */
  stickyMobile?: boolean;
  /** Call to action text */
  title?: React.ReactNode;
  /** Style pass-through */
  style?: HTMLAttributes<HTMLUListElement>['style'];
}

/**
 * Displays Buttons
 */
const Buttons: React.FunctionComponent<IButtonsProps> = (props) => {
  if (React.Children.toArray(props.children).length === 0) {
    return null;
  }
  return (
    <ul
      className={classNames(
        'buttons',
        {
          ['layout-default']: props.layout === 'default' || !props.layout,
          ['layout-horizontal']: props.layout === 'horizontal',
          ['layout-centered']: props.layout === 'centered',
          ['layout-vertical']: props.layout === 'vertical',
          ['layout-vertical-wide']: props.layout === 'vertical-wide',
          ['size-small']: props.size === 'small',

          ['sticky-mobile']: props.stickyMobile,
        },
        props.className,
      )}
      style={props.style}
    >
      {props.title && <li className={'title'}>{props.title}</li>}
      {React.Children.map(props.children, (maybeChild: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const RenderChildren = (child: any, primary: boolean, secondary: boolean): any => {
          if (child === null || typeof child === 'string' || typeof child === 'number' || typeof child === 'boolean') {
            return child;
          }

          if (child.type === React.Fragment) {
            return React.Children.map(child.props.children, (innerChild) => {
              return RenderChildren(innerChild, false, false);
            });
          }

          if (!child.type || !child.type.displayName) {
            if (typeof child.props !== 'undefined' && typeof child.type !== 'undefined') {
              return (
                <li
                  key={child.key}
                  className={classNames({
                    ['primary']: primary,
                    ['secondary']: secondary,
                  })}
                >
                  {child}
                </li>
              );
            }
            return child;
          }

          if (
            child.type.displayName === 'Button' ||
            child.type.displayName === 'ButtonIcon' ||
            child.type.displayName === 'ButtonWithIcon'
          ) {
            return (
              <li
                key={child.key}
                className={classNames({
                  ['primary']: primary,
                  ['secondary']: secondary,
                })}
              >
                {child}
              </li>
            );
          } else if (child.type.displayName === 'ButtonsPrimary' || child.type.displayName === 'ButtonsSecondary') {
            return React.Children.map(child.props.children, (sub) => {
              return RenderChildren(sub, child.type.displayName === 'ButtonsPrimary', child.type.displayName === 'ButtonsSecondary');
            });
          } else {
            return child;
          }
        };

        return RenderChildren(maybeChild, false, false);
      })}
    </ul>
  );
};

Buttons.displayName = 'Buttons';

export default Buttons;
